import { type ReactNode, useEffect, useId, useRef } from "react";

/**
 * A window over the page: every picker and every form-in-a-window. It is the
 * native `<dialog>` opened with `showModal()`, so the browser makes the rest
 * of the page inert, keeps focus inside and returns it to the opener; no
 * focus-trap code is written here.
 *
 * The parent's `open` is the single source of truth: the Close button, Esc
 * and a click on the backdrop only call `onClose()`. `children` and `footer`
 * are rendered only while it is open, so a dialog's inner state starts fresh
 * each time. Under 640 px it fills the screen.
 */
export function Dialog(props: {
  open: boolean;
  onClose(): void;
  title: string;
  size?: "default" | "large";
  children: ReactNode;
  /** The buttons row. */
  footer?: ReactNode;
}): JSX.Element {
  const { open, onClose, title, children, footer } = props;
  const id = useId();
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  // Esc: the element's own `cancel` event (React has no prop that every
  // browser honours for it). Prevented, so the element stays open until the
  // parent says otherwise.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (event: Event) => {
      event.preventDefault();
      close.current();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, []);

  return (
    <dialog
      ref={ref}
      className={`tb-dialog${props.size === "large" ? " tb-dialog--large" : ""}`}
      aria-labelledby={`${id}-title`}
      // The content covers the element's box, so only the backdrop produces
      // a click whose target is the dialog itself.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {open ? (
        <>
          <header className="tb-dialog-head">
            <h2 id={`${id}-title`}>{title}</h2>
            <button
              type="button"
              className="tb-dialog-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </header>
          <div className="tb-dialog-body">{children}</div>
          {footer !== undefined ? (
            <footer className="tb-dialog-foot">{footer}</footer>
          ) : null}
        </>
      ) : null}
    </dialog>
  );
}
